import * as THREE from 'three'
import { MM_PER_UNIT, SectionFrame } from './types'
import { frameMatrix, frameRoll, fromVector3, orthonormalise, toVector3, withRoll } from './frameMath'

/**
 * Direct manipulation for a section: drag to move, corners to resize, a handle to rotate.
 *
 * Every calculation happens in the FRAME'S OWN 2D SPACE. A ray from the pointer is
 * intersected with the frame's plane and converted into frame-local x/y, and from there a
 * move is a translation, a resize is two corners, and a rotation is an angle — the same
 * arithmetic whichever way the surface happens to face. Trying to do this in world space
 * means re-deriving the surface's orientation on every pointer move, which is where
 * gizmos usually start behaving differently on different faces.
 *
 * Hit-testing is done in that same 2D space rather than by raycasting against handle
 * meshes, so what the pointer grabs cannot drift from what is drawn.
 */

const ACCENT = 0x79bc61
const HANDLE_MIN_MM = 1.6
const HANDLE_MAX_MM = 5
/** Smallest section a drag may produce. Below this the gizmo has nothing to grab. */
const MIN_SIZE_MM = 2

export type DragMode = 'move' | 'resize' | 'rotate'

interface DragState {
  mode: DragMode
  /** Frame as it was when the drag began — every delta is measured against this. */
  start: SectionFrame
  startLocal: THREE.Vector2
  /** Resize only: the corner held still, in the start frame's local space. */
  anchor?: THREE.Vector2
  /** Rotate only: pointer angle at the start, radians. */
  startAngle?: number
  startRoll?: number
}

export class FrameGizmo extends THREE.Group {
  private outline: THREE.LineSegments
  private handles: THREE.Mesh[] = []
  private rotateHandle: THREE.Mesh
  private lineMaterial: THREE.LineBasicMaterial
  private handleMaterial: THREE.MeshBasicMaterial

  private frame: SectionFrame | null = null
  private root: THREE.Object3D | null = null
  private drag: DragState | null = null

  constructor() {
    super()
    // Drawn last and without depth testing: a gizmo that disappears inside the product
    // it is attached to is worse than one that floats.
    this.renderOrder = 1000
    this.matrixAutoUpdate = false

    this.lineMaterial = new THREE.LineBasicMaterial({ color: ACCENT, depthTest: false, transparent: true })
    this.handleMaterial = new THREE.MeshBasicMaterial({ color: ACCENT, depthTest: false, transparent: true })

    this.outline = new THREE.LineSegments(new THREE.BufferGeometry(), this.lineMaterial)
    this.outline.renderOrder = 1000
    this.add(this.outline)

    for (let i = 0; i < 4; i++) {
      const h = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.handleMaterial)
      h.renderOrder = 1001
      this.handles.push(h)
      this.add(h)
    }

    this.rotateHandle = new THREE.Mesh(new THREE.CircleGeometry(0.5, 20), this.handleMaterial)
    this.rotateHandle.renderOrder = 1001
    this.add(this.rotateHandle)
  }

  /** Half-extents and handle size for a frame, all in metres. */
  private metrics(frame: SectionFrame) {
    const halfW = frame.width / MM_PER_UNIT / 2
    const halfH = frame.height / MM_PER_UNIT / 2
    const smaller = Math.min(frame.width, frame.height)
    const handleMm = Math.min(Math.max(smaller * 0.12, HANDLE_MIN_MM), HANDLE_MAX_MM)
    const handle = handleMm / MM_PER_UNIT
    return { halfW, halfH, handle, rotateY: halfH + handle * 3 }
  }

  /** Point the gizmo at a frame, or hide it with `null`. */
  update(frame: SectionFrame | null, productRoot: THREE.Object3D | null): void {
    this.frame = frame
    this.root = productRoot
    this.visible = !!frame && !!productRoot
    if (!frame || !productRoot) return

    const { halfW, halfH, handle, rotateY } = this.metrics(frame)

    const pts: number[] = []
    const corners: [number, number][] = [
      [-halfW, -halfH],
      [halfW, -halfH],
      [halfW, halfH],
      [-halfW, halfH],
    ]
    for (let i = 0; i < 4; i++) {
      const a = corners[i]
      const b = corners[(i + 1) % 4]
      pts.push(a[0], a[1], 0, b[0], b[1], 0)
    }
    // Stem up to the rotate handle, so it reads as attached rather than floating.
    pts.push(0, halfH, 0, 0, rotateY, 0)

    this.outline.geometry.dispose()
    this.outline.geometry = new THREE.BufferGeometry().setAttribute(
      'position',
      new THREE.Float32BufferAttribute(pts, 3)
    )

    corners.forEach(([x, y], i) => {
      this.handles[i].position.set(x, y, 0)
      this.handles[i].scale.setScalar(handle)
    })
    this.rotateHandle.position.set(0, rotateY, 0)
    this.rotateHandle.scale.setScalar(handle)

    // The gizmo lives in the product root's space, same as the frame.
    this.matrix.copy(frameMatrix(frame))
    this.matrixWorldNeedsUpdate = true
  }

  /**
   * Where a pointer ray meets the frame's plane, in frame-local metres.
   *
   * Null when the ray runs parallel to the plane or meets it behind the camera — at which
   * point there is no meaningful answer, and guessing one makes the gizmo jump.
   */
  private localFromRay(ray: THREE.Ray, frame: SectionFrame): THREE.Vector2 | null {
    if (!this.root) return null
    this.root.updateMatrixWorld(true)

    const toRoot = new THREE.Matrix4().copy(this.root.matrixWorld).invert()
    const rootRay = new THREE.Ray(
      ray.origin.clone().applyMatrix4(toRoot),
      ray.direction.clone().transformDirection(toRoot).normalize()
    )

    const basis = orthonormalise(toVector3(frame.normal), toVector3(frame.up))
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(basis.forward, toVector3(frame.origin))

    const hit = rootRay.intersectPlane(plane, new THREE.Vector3())
    if (!hit) return null

    const local = hit.applyMatrix4(frameMatrix(frame).invert())
    return new THREE.Vector2(local.x, local.y)
  }

  /** What the pointer is over, without starting anything. */
  hitTest(ray: THREE.Ray): DragMode | null {
    if (!this.frame || !this.visible) return null
    const p = this.localFromRay(ray, this.frame)
    if (!p) return null

    const { halfW, halfH, handle, rotateY } = this.metrics(this.frame)
    const grab = handle * 1.3

    if (p.distanceTo(new THREE.Vector2(0, rotateY)) <= grab) return 'rotate'
    for (const [x, y] of [
      [-halfW, -halfH],
      [halfW, -halfH],
      [halfW, halfH],
      [-halfW, halfH],
    ]) {
      if (p.distanceTo(new THREE.Vector2(x, y)) <= grab) return 'resize'
    }
    if (Math.abs(p.x) <= halfW && Math.abs(p.y) <= halfH) return 'move'
    return null
  }

  /** Returns true when a drag started, so the caller can suspend camera controls. */
  beginDrag(ray: THREE.Ray): boolean {
    if (!this.frame) return false
    const mode = this.hitTest(ray)
    if (!mode) return false

    const p = this.localFromRay(ray, this.frame)
    if (!p) return false

    const { halfW, halfH } = this.metrics(this.frame)
    const state: DragState = { mode, start: { ...this.frame }, startLocal: p }

    if (mode === 'resize') {
      // The corner diagonally opposite the one grabbed stays put, so the section grows
      // away from the hand rather than around its centre.
      state.anchor = new THREE.Vector2(p.x >= 0 ? -halfW : halfW, p.y >= 0 ? -halfH : halfH)
    } else if (mode === 'rotate') {
      state.startAngle = Math.atan2(p.x, p.y)
      state.startRoll = frameRoll(this.frame)
    }

    this.drag = state
    return true
  }

  /** The frame as the drag currently has it, or null if nothing is being dragged. */
  updateDrag(ray: THREE.Ray): SectionFrame | null {
    const d = this.drag
    if (!d) return null

    const p = this.localFromRay(ray, d.start)
    if (!p) return null

    const basis = orthonormalise(toVector3(d.start.normal), toVector3(d.start.up))
    const minSize = MIN_SIZE_MM / MM_PER_UNIT

    if (d.mode === 'move') {
      const dx = p.x - d.startLocal.x
      const dy = p.y - d.startLocal.y
      const moved = toVector3(d.start.origin)
        .add(basis.right.clone().multiplyScalar(dx))
        .add(basis.up.clone().multiplyScalar(dy))
      return { ...d.start, origin: fromVector3(moved) }
    }

    if (d.mode === 'resize' && d.anchor) {
      const w = Math.max(Math.abs(p.x - d.anchor.x), minSize)
      const h = Math.max(Math.abs(p.y - d.anchor.y), minSize)
      // Centre sits between the held corner and the dragged one.
      const cx = (p.x + d.anchor.x) / 2
      const cy = (p.y + d.anchor.y) / 2
      const moved = toVector3(d.start.origin)
        .add(basis.right.clone().multiplyScalar(cx))
        .add(basis.up.clone().multiplyScalar(cy))
      return {
        ...d.start,
        origin: fromVector3(moved),
        width: w * MM_PER_UNIT,
        height: h * MM_PER_UNIT,
      }
    }

    if (d.mode === 'rotate' && d.startAngle !== undefined && d.startRoll !== undefined) {
      const delta = THREE.MathUtils.radToDeg(Math.atan2(p.x, p.y) - d.startAngle)
      return withRoll(d.start, d.startRoll + delta)
    }

    return null
  }

  endDrag(): void {
    this.drag = null
  }

  get dragging(): boolean {
    return this.drag !== null
  }

  dispose(): void {
    this.outline.geometry.dispose()
    this.handles.forEach((h) => h.geometry.dispose())
    this.rotateHandle.geometry.dispose()
    this.lineMaterial.dispose()
    this.handleMaterial.dispose()
  }
}
